import { FaceTagRow } from '../../../components/FaceTagRow';
import { TemplatePicker } from '../../../components/TemplatePicker';
import { getEventTemplate, type TemplateKind } from '../../../utils/eventTemplates';
import type { Member } from '../../../types';
import type { ClusterState } from './types';
import type { SuggestedEventMatch } from '../../../utils/existingEventMatch';

interface ClusterReviewCardProps {
  cluster: ClusterState;
  members: Member[];
  disabled?: boolean;
  suggestedMatch: SuggestedEventMatch | null;
  attachedEventTitle: string | null;
  onAttachToExisting: (eventId: string) => void;
  onUndoAttach: () => void;
  onFieldChange: (field: 'title' | 'description' | 'eventDate' | 'location', value: string) => void;
  onToggleExpanded: () => void;
  onTogglePhoto: (clientId: string) => void;
  onAssignFace: (photoClientId: string, faceIdx: number, userId: string | null) => void;
  onAddNewPerson: (photoClientId: string, faceIdx: number, name: string) => Promise<void>;
  onExcludePhoto: (clientId: string) => void;
  onRemoveCluster: () => void;
  onMergeWithNext: () => void;
  onMoveToNewEvent: (clientId: string) => void;
  onTemplateKindChange: (kind: TemplateKind) => void;
  onDismissTemplateSuggestion: () => void;
  onPhotoCaptionChange: (photoClientId: string, value: string) => void;
}

export function ClusterReviewCard({
  cluster, members, disabled, suggestedMatch, attachedEventTitle, onAttachToExisting, onUndoAttach,
  onFieldChange, onToggleExpanded, onTogglePhoto,
  onAssignFace, onAddNewPerson, onExcludePhoto,
  onRemoveCluster, onMergeWithNext, onMoveToNewEvent,
  onTemplateKindChange, onDismissTemplateSuggestion, onPhotoCaptionChange,
}: ClusterReviewCardProps) {
  const humanMembers = members.filter(m => !m.petType && (m.status === 'active' || m.status === 'no_email' || m.status === 'pending'));
  const selected = cluster.photos.filter(p => cluster.selectedIds.has(p.clientId));
  const rest = cluster.photos.filter(p => !cluster.selectedIds.has(p.clientId));
  const visiblePhotos = cluster.expanded ? cluster.photos : selected;
  const facesToShow = selected.filter(p => (cluster.facesByPhoto[p.clientId] ?? []).length > 0);

  return (
    <div className={`rounded-xl border border-indigo-100 bg-white p-4 ${disabled ? 'opacity-60 pointer-events-none' : ''}`}>
      {suggestedMatch && (
        <div className="flex flex-wrap items-center gap-2 mb-3 px-3 py-2 rounded-lg bg-indigo-50 text-xs text-indigo-700">
          <span>
            Looks like this matches <strong>&ldquo;{suggestedMatch.eventTitle}&rdquo;</strong>
            {suggestedMatch.eventDate && <> ({suggestedMatch.eventDate})</>} — attach these photos there instead?
          </span>
          <button
            type="button"
            onClick={() => onAttachToExisting(suggestedMatch.eventId)}
            className="ml-auto px-2.5 py-1 rounded-md bg-indigo-600 text-white font-semibold hover:bg-indigo-700 transition-colors"
          >
            Attach
          </button>
        </div>
      )}
      {cluster.suggestedTemplateKind && !cluster.templateKindEdited && !cluster.suggestedTemplateDismissed && !attachedEventTitle && (
        <div className="flex flex-wrap items-center gap-2 mb-3 px-3 py-2 rounded-lg bg-indigo-50 text-xs text-indigo-700">
          <span>
            {getEventTemplate(cluster.suggestedTemplateKind.kind).icon} This looks like a <strong>{getEventTemplate(cluster.suggestedTemplateKind.kind).label}</strong> — use that template?
          </span>
          <button
            type="button"
            onClick={() => onTemplateKindChange(cluster.suggestedTemplateKind!.kind)}
            className="ml-auto px-2.5 py-1 rounded-md bg-indigo-600 text-white font-semibold hover:bg-indigo-700 transition-colors"
          >
            Use {getEventTemplate(cluster.suggestedTemplateKind.kind).label}
          </button>
          <button
            type="button"
            onClick={onDismissTemplateSuggestion}
            className="px-2 py-1 text-indigo-500 hover:text-indigo-700 font-medium"
          >
            Dismiss
          </button>
        </div>
      )}
      {attachedEventTitle && (
        <div className="flex flex-wrap items-center gap-2 mb-3 px-3 py-2 rounded-lg bg-emerald-50 text-xs text-emerald-700">
          <span>
            Attaching {cluster.selectedIds.size} photo{cluster.selectedIds.size === 1 ? '' : 's'} to <strong>&ldquo;{attachedEventTitle}&rdquo;</strong>
          </span>
          <button
            type="button"
            onClick={onUndoAttach}
            className="ml-auto px-2.5 py-1 rounded-md bg-white border border-emerald-300 text-emerald-700 font-semibold hover:bg-emerald-100 transition-colors"
          >
            Undo
          </button>
        </div>
      )}
      <div className="flex flex-col sm:flex-row gap-4">
        <div className="flex gap-1.5 overflow-x-auto sm:flex-wrap sm:w-32 flex-shrink-0 pb-1 sm:pb-0">
          {visiblePhotos.map(photo => {
            const isChecked = cluster.selectedIds.has(photo.clientId);
            return (
              <button
                key={photo.clientId}
                type="button"
                onClick={() => onTogglePhoto(photo.clientId)}
                title={isChecked ? 'Included — tap to exclude' : 'Not included — tap to include'}
                className={`relative flex-shrink-0 w-14 h-14 rounded-lg overflow-hidden border-2 ${isChecked ? 'border-indigo-500' : 'border-slate-200 opacity-50'}`}
              >
                <img src={photo.thumbDataUrl} alt="" className="w-full h-full object-cover" />
                {isChecked && (
                  <span className="absolute top-0.5 right-0.5 w-3.5 h-3.5 rounded-full bg-indigo-600 text-white text-[8px] flex items-center justify-center">✓</span>
                )}
              </button>
            );
          })}
        </div>

        {!attachedEventTitle && (
          <div className="flex-1 min-w-0 space-y-2">
            <TemplatePicker value={cluster.templateKind} onChange={onTemplateKindChange} />
            <input
              type="text"
              data-testid="cluster-title-input"
              value={cluster.title}
              onChange={e => onFieldChange('title', e.target.value)}
              className="w-full text-sm font-semibold border border-slate-200 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-indigo-500"
            />
            <div className="flex flex-col sm:flex-row gap-2">
              <input
                type="date"
                value={cluster.eventDate}
                onChange={e => onFieldChange('eventDate', e.target.value)}
                className="flex-1 text-xs border border-slate-200 rounded-lg px-3 py-1.5 focus:outline-none focus:ring-2 focus:ring-indigo-500"
              />
              <input
                type="text"
                value={cluster.geocoding ? 'Finding location…' : cluster.location}
                onChange={e => onFieldChange('location', e.target.value)}
                placeholder="Location"
                disabled={cluster.geocoding}
                className="flex-1 text-xs border border-slate-200 rounded-lg px-3 py-1.5 focus:outline-none focus:ring-2 focus:ring-indigo-500 disabled:text-slate-400"
              />
            </div>
            <textarea
              value={cluster.description}
              onChange={e => onFieldChange('description', e.target.value)}
              rows={2}
              className="w-full text-xs border border-slate-200 rounded-lg px-3 py-1.5 resize-none focus:outline-none focus:ring-2 focus:ring-indigo-500"
            />
          </div>
        )}
      </div>

      {cluster.detecting && (
        <p className="flex items-center gap-1.5 text-xs text-violet-600 font-medium mt-3">
          <span className="w-1.5 h-1.5 rounded-full bg-violet-500 animate-pulse" />
          Detecting faces…
        </p>
      )}

      {facesToShow.map(photo => (
        <div key={photo.clientId} className="mt-3 flex gap-2">
          <img src={photo.thumbDataUrl} alt="" className="w-8 h-8 rounded object-cover flex-shrink-0 mt-0.5" />
          <div className="flex-1 min-w-0 space-y-1.5">
            <FaceTagRow
              faces={cluster.facesByPhoto[photo.clientId] ?? []}
              members={humanMembers}
              onAssign={(faceIdx, userId) => onAssignFace(photo.clientId, faceIdx, userId)}
              onAddNewPerson={(faceIdx, name) => onAddNewPerson(photo.clientId, faceIdx, name)}
            />
            <input
              type="text"
              value={cluster.captionsByPhoto[photo.clientId] ?? ''}
              onChange={e => onPhotoCaptionChange(photo.clientId, e.target.value)}
              placeholder="Add a caption…"
              className="w-full text-xs border border-slate-200 rounded-lg px-2.5 py-1 focus:outline-none focus:ring-2 focus:ring-indigo-500"
            />
          </div>
        </div>
      ))}

      {rest.length > 0 && (
        <button
          type="button"
          onClick={onToggleExpanded}
          className="mt-3 text-xs font-medium text-indigo-600 hover:text-indigo-800"
        >
          {cluster.expanded
            ? `Hide extra photos (${selected.length} featured · ${rest.length} more)`
            : `${selected.length} featured · ${rest.length} more in this event →`}
        </button>
      )}

      {cluster.expanded && (
        <p className="mt-1 text-[11px] text-slate-400">
          Tap any thumbnail above to include or exclude it from the import.
        </p>
      )}

      <div className="flex flex-wrap items-center gap-3 mt-3 pt-3 border-t border-slate-100">
        <button type="button" onClick={onMergeWithNext} className="text-xs font-medium text-indigo-600 hover:text-indigo-800">
          ⊕ Merge with next
        </button>
        {selected.length > 1 && (
          <span className="text-xs text-slate-400">Tap a thumbnail's × below to move it to its own event</span>
        )}
        <button type="button" onClick={onRemoveCluster} className="text-xs font-medium text-slate-400 hover:text-red-600 ml-auto">
          ✕ Remove event
        </button>
      </div>

      {cluster.expanded && cluster.photos.length > 1 && (
        <div className="flex flex-wrap gap-2 mt-2">
          {cluster.photos.map(photo => (
            <div key={photo.clientId} className="relative">
              <button
                type="button"
                onClick={() => onMoveToNewEvent(photo.clientId)}
                title="Move to its own event"
                className="absolute -top-1.5 -right-1.5 w-4 h-4 rounded-full bg-white border border-slate-300 text-[9px] text-slate-500 hover:text-red-600 hover:border-red-300 flex items-center justify-center z-10"
              >
                ✂
              </button>
              <button
                type="button"
                onClick={() => onExcludePhoto(photo.clientId)}
                title="Remove this photo entirely"
                className="w-10 h-10 rounded overflow-hidden border border-slate-200"
              >
                <img src={photo.thumbDataUrl} alt="" className="w-full h-full object-cover" />
              </button>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
